import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { LanguageProvider, useLang } from './i18n';
import { isEsPath } from './data/routes';
import { LANDING_LIST_ALL } from './data/landings';
import SmoothScroll from './components/SmoothScroll';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import MobileCallBar from './components/MobileCallBar';
import UrgentCall from './components/UrgentCall';
import WhatsAppButton from './components/WhatsAppButton';
import HomePage from './pages/HomePage';
const WalkInPage = lazy(() => import('./pages/WalkInPage'));
const PersonalInjuryPage = lazy(() => import('./pages/PersonalInjuryPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const ProgramsPage = lazy(() => import('./pages/ProgramsPage'));
const TechnologyPage = lazy(() => import('./pages/TechnologyPage'));
const DecompressionPage = lazy(() => import('./pages/DecompressionPage'));
const ConditionsPage = lazy(() => import('./pages/ConditionsPage'));
const ConditionPage = lazy(() => import('./pages/ConditionPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LocationsIndex = lazy(() => import('./pages/LocationsIndex'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Keep the active language in sync with the URL (/es/... => Spanish).
const LangRouteSync = () => {
  const location = useLocation();
  const { lang, setLang } = useLang();
  useEffect(() => {
    const urlLang = isEsPath(location.pathname) ? 'es' : 'en';
    if (urlLang !== lang) setLang(urlLang);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

// Scroll to top on every route change (Lenis-aware).
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<div style={{ minHeight: '100vh', background: '#F2F0ED' }} />}>
        <Routes location={location} key={location.pathname}>
        {/* English */}
        <Route path="/" element={<HomePage />} />
        <Route path="/walk-in" element={<WalkInPage />} />
        <Route path="/personal-injury" element={<PersonalInjuryPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/programs" element={<ProgramsPage />} />
        <Route path="/technology" element={<TechnologyPage />} />
        <Route path="/decompression" element={<DecompressionPage />} />
        <Route path="/conditions" element={<ConditionsPage />} />
        <Route path="/conditions/:slug" element={<ConditionPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        {/* Spanish */}
        <Route path="/es" element={<HomePage />} />
        <Route path="/es/atencion-sin-cita" element={<WalkInPage />} />
        <Route path="/es/lesiones-personales" element={<PersonalInjuryPage />} />
        <Route path="/es/servicios" element={<ServicesPage />} />
        <Route path="/es/programas" element={<ProgramsPage />} />
        <Route path="/es/tecnologia" element={<TechnologyPage />} />
        <Route path="/es/descompresion" element={<DecompressionPage />} />
        <Route path="/es/tratamientos/:slug" element={<ConditionPage />} />
        <Route path="/es/contacto" element={<ContactPage />} />
        {/* Local SEO — Austin areas + head-term landing pages */}
        <Route path="/locations" element={<LocationsIndex />} />
        <Route path="/locations/:slug" element={<LandingPage />} />
        {LANDING_LIST_ALL.map((d) => (
          <Route key={d.path} path={d.path} element={<LandingPage data={d} />} />
        ))}
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <BrowserRouter>
          <LangRouteSync />
          <SmoothScroll>
            <ScrollToTop />
            <Navigation />
            <main style={{ background: '#F2F0ED' }}>
              <AnimatedRoutes />
            </main>
            <Footer />
            <MobileCallBar />
            <UrgentCall />
            <WhatsAppButton />
            <Toaster position="top-center" />
          </SmoothScroll>
        </BrowserRouter>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
